import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { MarkantElementsModule } from '@markant/elements-angular';
import { TranslocoModule, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ColumnSelectItem, HeaderAndTableCellDraft } from '@markant-platform/shared/table/model';
import { toSignal } from '@angular/core/rxjs-interop';
import { ArrayIncludesPipe } from '@markant-platform/shared/util-pipes';
import { ModalBridgeComponent } from '@markant-platform/shared/ui-modal';

@Component({
  selector: 'markant-platform-table-column-select',
  standalone: true,
  imports: [MarkantElementsModule, TranslocoPipe, TranslocoModule, ArrayIncludesPipe, ModalBridgeComponent],
  templateUrl: './table-column-select.component.html',
  styleUrl: './table-column-select.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableColumnSelectComponent {
  tableColumns = input.required<HeaderAndTableCellDraft[]>();
  hiddenColumns = input<string[]>([]);
  defaultHiddenColumns = input<string[] | undefined>([]);

  columnsChange = output<string[]>();

  open = false;
  selectedColumns: string[] = [];

  private readonly translocoService: TranslocoService = inject(TranslocoService);

  private search = signal<string>('');
  private lang = toSignal(this.translocoService.langChanges$, { initialValue: this.translocoService.getActiveLang() });

  columns = computed(() =>
    this.extractFilterOptions(this.tableColumns(), this.lang())
      .filter(col => col.label.toLocaleLowerCase().includes(this.search().toLocaleLowerCase()))
      .sort((col1, col2) => this.compareForSortingBySelectionAndAlphabetically(col1, col2, this.hiddenColumns()))
  );

  preselectedColumns = computed(() =>
    this.extractFilterOptions(this.tableColumns(), this.lang())
      .map(({ columnId }) => columnId)
      .filter(columnId => !this.hiddenColumns()?.includes(columnId))
  );

  constructor() {
    effect(() => {
      this.selectedColumns = [...this.preselectedColumns()];
    });
  }

  togglePopover(): void {
    this.open = !this.open;
  }

  closePopover(): void {
    this.selectedColumns = [...this.preselectedColumns()];
    this.open = false;
  }

  onSelectionChanged(column: string): void {
    let columns = [...this.selectedColumns];

    if (columns.includes(column)) {
      columns = columns.filter(col => col !== column);
    } else {
      columns.push(column);
    }

    if (!columns.length) {
      columns = this.restoreDefaultSelection();
    }

    this.selectedColumns = columns;
  }

  onSearchChanged(search = ''): void {
    this.search.set(search);
  }

  apply(): void {
    this.columnsChange.emit(this.selectedColumns);
    this.closePopover();
  }

  reset(): void {
    this.columnsChange.emit(this.restoreDefaultSelection());
    this.closePopover();
  }

  private compareForSortingBySelectionAndAlphabetically(
    { columnId: colId1, label: label1 }: ColumnSelectItem,
    { columnId: colId2, label: label2 }: ColumnSelectItem,
    hiddenColumns: string[]
  ): number {
    if (!hiddenColumns.includes(colId1) && !hiddenColumns.includes(colId2)) {
      return 0;
    }
    if (!hiddenColumns.includes(colId1)) {
      return -1;
    }
    if (!hiddenColumns.includes(colId2)) {
      return 1;
    }
    return label1.localeCompare(label2);
  }

  private extractFilterOptions(headerAndTableCells: HeaderAndTableCellDraft[], lang: string): ColumnSelectItem[] {
    return headerAndTableCells
      .filter(cell => cell.headerCellColumnId !== 'actions')
      .map(cell => ({
        columnId: cell.headerCellColumnId,
        label: this.translocoService.translate(cell.headerCellTranslationKey, undefined, lang),
      }));
  }

  private restoreDefaultSelection(): string[] {
    return this.columns()
      .filter(({ columnId }) => !this.defaultHiddenColumns()?.includes(columnId))
      .map(({ columnId }) => columnId);
  }
}
